/* eslint-disable no-case-declarations */
 
 
/* eslint-disable no-prototype-builtins */
 
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { z, ZodSchema } from 'zod';
import { DatabaseService } from './DatabaseService';
import { getDb } from '../db/connection';
import { GameSave, gameSaveSchema } from '../schemas/gameSave';
import { productSchema } from '../schemas/product';
import { StoreState } from '../stores/types';

// Persistence configuration types
export interface PersistenceConfig {
  debounceTime: number;
  maxRetries: number;
  retryDelay: number;
  batchSize: number;
  compressionEnabled: boolean;
  encryptionEnabled: boolean;
}

// Storage routing configuration
export interface StorageRoutingConfig {
  localStorage: string[];
  indexedDB: string[];
}

// Batch operation types
export interface BatchOperation {
  id: string;
  type: 'save' | 'load' | 'delete';
  storage: 'localStorage' | 'indexedDB';
  key: string;
  data?: unknown;
  timestamp: number;
  priority: 'low' | 'normal' | 'high';
  retryCount: number;
}

// Conflict resolution types
export interface ConflictResolution {
  strategy: 'latest' | 'merge' | 'custom';
  resolver?: (local: unknown, remote: unknown) => unknown;
}

// Migration types
export interface MigrationUtility {
  version: number;
  up: (data: unknown) => unknown;
  down: (data: unknown) => unknown;
  schema?: ZodSchema;
}

// Performance metrics
export interface PerformanceMetrics {
  operationType: string;
  duration: number;
  dataSize: number;
  timestamp: number;
  success: boolean;
  error?: string;
}

// Sync operation result
export interface SyncResult {
  success: boolean;
  operationId: string;
  duration: number;
  error?: Error;
  conflicts?: Array<{
    key: string;
    resolved: boolean;
    strategy: string;
  }>;
}

// Default configuration
const DEFAULT_CONFIG: PersistenceConfig = {
  debounceTime: 1000,
  maxRetries: 3,
  retryDelay: 1000,
  batchSize: 50,
  compressionEnabled: false,
  encryptionEnabled: false,
};

// Default storage routing
const DEFAULT_ROUTING: StorageRoutingConfig = {
  localStorage: ['gameState', 'uiState', 'settings', 'preferences'],
  indexedDB: ['products', 'inventory', 'production', 'market', 'company'],
};

export class PersistenceService {
  private static instance: PersistenceService;
  private config: PersistenceConfig;
  private routing: StorageRoutingConfig;
  private operationQueue: BatchOperation[] = [];
  private syncTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private performanceMetrics: PerformanceMetrics[] = [];
  private migrations: Map<string, MigrationUtility[]> = new Map();
  private schemas: Map<string, ZodSchema> = new Map();
  private isProcessingQueue = false;
  private listeners: Set<(event: string, data: unknown) => void> = new Set();

  private constructor(
    config: Partial<PersistenceConfig> = {},
    routing: Partial<StorageRoutingConfig> = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.routing = {
      localStorage: [...(routing.localStorage || DEFAULT_ROUTING.localStorage)],
      indexedDB: [...(routing.indexedDB || DEFAULT_ROUTING.indexedDB)],
    };

    // Register default schemas
    this.registerSchema('gameState', gameSaveSchema);
    
    // Initialize persistence store in IndexedDB if needed
    this.initializePersistenceStore();
  }

  static getInstance(
    config?: Partial<PersistenceConfig>,
    routing?: Partial<StorageRoutingConfig>
  ): PersistenceService {
    if (!PersistenceService.instance) {
      PersistenceService.instance = new PersistenceService(config, routing);
    }
    return PersistenceService.instance;
  }

  // Initialize persistence store in IndexedDB
  private async initializePersistenceStore(): Promise<void> {
    try {
      const db = await getDb();
      const version = db.version;
      
      // Check if persistence store exists
      if (!db.objectStoreNames.contains('persistence')) {
        // Need to increment version and add store
        db.close();
        
        // Reopen with incremented version
        const { openDB } = await import('idb');
        await openDB('phoneverse', version + 1, {
          upgrade(database) {
            if (!database.objectStoreNames.contains('persistence')) {
              const persistenceStore = database.createObjectStore('persistence', { keyPath: 'id' });
              persistenceStore.createIndex('timestamp', 'timestamp');
              persistenceStore.createIndex('type', 'type');
            }
          }
        });
      }
    } catch (error) {
      console.error('Failed to initialize persistence store:', error);
    }
  }

  // Register a schema for validation
  registerSchema(key: string, schema: ZodSchema): void {
    this.schemas.set(key, schema);
  }

  // Register migrations for a key
  registerMigrations(key: string, migrations: MigrationUtility[]): void {
    this.migrations.set(key, migrations.sort((a, b) => a.version - b.version));
  }

  // Main sync methods
  async syncToIndexedDB(key: string, data: unknown, options?: {
    validate?: boolean;
    debounce?: boolean;
    priority?: 'low' | 'normal' | 'high';
  }): Promise<SyncResult> {
    const startTime = performance.now();
    const operationId = this.generateOperationId();

    try {
      // Validate data if schema exists and validation is enabled
      if (options?.validate !== false && this.schemas.has(key)) {
        const schema = this.schemas.get(key)!;
        const validationResult = schema.safeParse(data);
        
        if (!validationResult.success) {
          throw new Error(`Validation failed: ${validationResult.error.message}`);
        }
      }

      // Add to batch queue if debounce is enabled
      if (options?.debounce !== false) {
        return this.queueOperation({
          id: operationId,
          type: 'save',
          storage: 'indexedDB',
          key,
          data,
          timestamp: Date.now(),
          priority: options?.priority || 'normal',
          retryCount: 0,
        });
      }

      // Direct save
      await this.saveToIndexedDB(key, data);
      
      const duration = performance.now() - startTime;
      this.recordMetric('syncToIndexedDB', duration, JSON.stringify(data).length, true);

      return {
        success: true,
        operationId,
        duration,
      };
    } catch (error) {
      const duration = performance.now() - startTime;
      this.recordMetric('syncToIndexedDB', duration, 0, false, error.message);

      return {
        success: false,
        operationId,
        duration,
        error: error as Error,
      };
    }
  }

  async syncToLocalStorage(key: string, data: unknown, options?: {
    validate?: boolean;
    debounce?: boolean;
    priority?: 'low' | 'normal' | 'high';
  }): Promise<SyncResult> {
    const startTime = performance.now();
    const operationId = this.generateOperationId();

    try {
      // Validate data if schema exists and validation is enabled
      if (options?.validate !== false && this.schemas.has(key)) {
        const schema = this.schemas.get(key)!;
        const validationResult = schema.safeParse(data);
        
        if (!validationResult.success) {
          throw new Error(`Validation failed: ${validationResult.error.message}`);
        }
      }

      // Add to batch queue if debounce is enabled
      if (options?.debounce !== false) {
        return this.queueOperation({
          id: operationId,
          type: 'save',
          storage: 'localStorage',
          key,
          data,
          timestamp: Date.now(),
          priority: options?.priority || 'normal',
          retryCount: 0,
        });
      }

      // Direct save
      await this.saveToLocalStorage(key, data);
      
      const duration = performance.now() - startTime;
      this.recordMetric('syncToLocalStorage', duration, JSON.stringify(data).length, true);

      return {
        success: true,
        operationId,
        duration,
      };
    } catch (error) {
      const duration = performance.now() - startTime;
      this.recordMetric('syncToLocalStorage', duration, 0, false, error.message);

      return {
        success: false,
        operationId,
        duration,
        error: error as Error,
      };
    }
  }

  async loadFromIndexedDB<T = unknown>(key: string, options?: {
    validate?: boolean;
    migrate?: boolean;
  }): Promise<T | null> {
    const startTime = performance.now();

    try {
      const data = await DatabaseService.get('persistence', key);
      
      if (!data) {
        return null;
      }

      let result = data.state;

      // Apply migrations if needed
      if (options?.migrate !== false && this.migrations.has(key)) {
        result = await this.applyMigrations(key, result, data.version || 1);
      }

      // Validate if schema exists
      if (options?.validate !== false && this.schemas.has(key)) {
        const schema = this.schemas.get(key)!;
        const validationResult = schema.safeParse(result);
        
        if (!validationResult.success) {
          throw new Error(`Validation failed: ${validationResult.error.message}`);
        }
        
        result = validationResult.data;
      }

      const duration = performance.now() - startTime;
      this.recordMetric('loadFromIndexedDB', duration, JSON.stringify(result).length, true);

      return result as T;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.recordMetric('loadFromIndexedDB', duration, 0, false, error.message);
      throw error;
    }
  }

  async loadFromLocalStorage<T = unknown>(key: string, options?: {
    validate?: boolean;
    migrate?: boolean;
  }): Promise<T | null> {
    const startTime = performance.now();

    try {
      const item = localStorage.getItem(key);
      
      if (!item) {
        return null;
      }

      const parsed = JSON.parse(item);
      let result = parsed.state;

      // Apply migrations if needed
      if (options?.migrate !== false && this.migrations.has(key)) {
        result = await this.applyMigrations(key, result, parsed.version || 1);
      }

      // Validate if schema exists
      if (options?.validate !== false && this.schemas.has(key)) {
        const schema = this.schemas.get(key)!;
        const validationResult = schema.safeParse(result);
        
        if (!validationResult.success) {
          throw new Error(`Validation failed: ${validationResult.error.message}`);
        }
        
        result = validationResult.data;
      }

      const duration = performance.now() - startTime;
      this.recordMetric('loadFromLocalStorage', duration, JSON.stringify(result).length, true);

      return result as T;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.recordMetric('loadFromLocalStorage', duration, 0, false, error.message);
      throw error;
    }
  }

  // Batch operations
  async batchSync(operations: Array<{
    key: string;
    data: unknown;
    storage?: 'localStorage' | 'indexedDB';
    priority?: 'low' | 'normal' | 'high';
  }>): Promise<SyncResult[]> {
    const results: SyncResult[] = [];
    const batches = this.createBatches(operations, this.config.batchSize);

    for (const batch of batches) {
      const batchResults = await Promise.allSettled(
        batch.map(op => {
          const storage = op.storage || this.determineStorage(op.key);
          
          if (storage === 'localStorage') {
            return this.syncToLocalStorage(op.key, op.data, {
              debounce: false,
              priority: op.priority,
            });
          } else {
            return this.syncToIndexedDB(op.key, op.data, {
              debounce: false,
              priority: op.priority,
            });
          }
        })
      );

      results.push(...batchResults.map(result => 
        result.status === 'fulfilled' ? result.value : {
          success: false,
          operationId: this.generateOperationId(),
          duration: 0,
          error: result.reason,
        }
      ));
    }

    return results;
  }

  // Queue management
  private async queueOperation(operation: BatchOperation): Promise<SyncResult> {
    this.operationQueue.push(operation);
    
    // Sort queue by priority and timestamp
    this.operationQueue.sort((a, b) => {
      const priorityOrder = { high: 0, normal: 1, low: 2 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      
      return a.timestamp - b.timestamp;
    });

    // Debounce processing
    const timeoutKey = `${operation.storage}-${operation.key}`;
    
    if (this.syncTimeouts.has(timeoutKey)) {
      clearTimeout(this.syncTimeouts.get(timeoutKey)!);
    }

    const timeout = setTimeout(() => {
      this.processQueue();
      this.syncTimeouts.delete(timeoutKey);
    }, this.config.debounceTime);

    this.syncTimeouts.set(timeoutKey, timeout);

    return {
      success: true,
      operationId: operation.id,
      duration: 0,
    };
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.operationQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    try {
      while (this.operationQueue.length > 0) {
        const batch = this.operationQueue.splice(0, this.config.batchSize);
        
        await Promise.allSettled(
          batch.map(op => this.processOperation(op))
        );
      }
    } finally {
      this.isProcessingQueue = false;
    }
  }

  private async processOperation(operation: BatchOperation): Promise<void> {
    try {
      if (operation.type === 'save') {
        if (operation.storage === 'localStorage') {
          await this.saveToLocalStorage(operation.key, operation.data);
        } else {
          await this.saveToIndexedDB(operation.key, operation.data);
        }
      }
      
      this.emit('operationComplete', operation);
    } catch (error) {
      if (operation.retryCount < this.config.maxRetries) {
        // Retry with exponential backoff
        setTimeout(() => {
          operation.retryCount++;
          this.operationQueue.push(operation);
        }, this.config.retryDelay * Math.pow(2, operation.retryCount));
      } else {
        this.emit('operationFailed', { operation, error });
      }
    }
  }

  // Storage operations
  private async saveToIndexedDB(key: string, data: unknown): Promise<void> {
    try {
      const record = {
        state: data,
        version: this.getDataVersion(key),
        timestamp: Date.now(),
        type: 'state',
      };

      const existing = await DatabaseService.get('persistence', key);
      
      if (existing) {
        await DatabaseService.update('persistence', key, record);
      } else {
        await DatabaseService.add('persistence', { ...record, id: key });
      }
    } catch (error: any) {
      // Handle database version mismatch
      if (error?.message?.includes('version') || error?.message?.includes('object store')) {
        console.error('Database version mismatch detected. Marking for cleanup.');
        localStorage.setItem('dbNeedsClearing', 'true');
        // Fall back to localStorage
        await this.saveToLocalStorage(key, data);
      } else {
        throw error;
      }
    }
  }

  private async saveToLocalStorage(key: string, data: unknown): Promise<void> {
    const record = {
      state: data,
      version: this.getDataVersion(key),
      timestamp: Date.now(),
    };

    localStorage.setItem(key, JSON.stringify(record));
  }

  // Migration utilities
  private async applyMigrations(key: string, data: unknown, currentVersion: number): Promise<unknown> {
    const migrations = this.migrations.get(key) || [];
    let result = data;

    for (const migration of migrations) {
      if (migration.version > currentVersion) {
        result = migration.up(result);
        
        // Validate after migration if schema provided
        if (migration.schema) {
          const validationResult = migration.schema.safeParse(result);
          
          if (!validationResult.success) {
            throw new Error(`Migration validation failed: ${validationResult.error.message}`);
          }
          
          result = validationResult.data;
        }
      }
    }

    return result;
  }

  // Conflict resolution
  async resolveConflict(
    key: string,
    localData: unknown,
    remoteData: unknown,
    resolution: ConflictResolution
  ): Promise<unknown> {
    switch (resolution.strategy) {
      case 'latest':
        // Compare timestamps and use the latest
        const localTimestamp = (localData as any)?.timestamp || 0;
        const remoteTimestamp = (remoteData as any)?.timestamp || 0;
        return localTimestamp > remoteTimestamp ? localData : remoteData;

      case 'merge':
        // Deep merge objects
        if (typeof localData === 'object' && typeof remoteData === 'object') {
          return this.deepMerge(localData as Record<string, unknown>, remoteData as Record<string, unknown>);
        }
        return remoteData;

      case 'custom':
        if (resolution.resolver) {
          return resolution.resolver(localData, remoteData);
        }
        throw new Error('Custom resolver not provided');

      default:
        return remoteData;
    }
  }

  // Utility methods
  private determineStorage(key: string): 'localStorage' | 'indexedDB' {
    if (this.routing.localStorage.includes(key)) {
      return 'localStorage';
    }
    
    if (this.routing.indexedDB.includes(key)) {
      return 'indexedDB';
    }
    
    // Default to localStorage for smaller data
    return 'localStorage';
  }

  private generateOperationId(): string {
    return `op-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private getDataVersion(key: string): number {
    const migrations = this.migrations.get(key) || [];
    return migrations.length > 0 ? migrations[migrations.length - 1].version : 1;
  }

  private createBatches<T>(items: T[], batchSize: number): T[][] {
    const batches: T[][] = [];
    
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
    }
    
    return batches;
  }

  private deepMerge(
    target: Record<string, unknown>,
    source: Record<string, unknown>
  ): Record<string, unknown> {
    const result = { ...target };

    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (
          typeof source[key] === 'object' &&
          source[key] !== null &&
          !Array.isArray(source[key]) &&
          typeof result[key] === 'object' &&
          result[key] !== null &&
          !Array.isArray(result[key])
        ) {
          result[key] = this.deepMerge(
            result[key] as Record<string, unknown>,
            source[key] as Record<string, unknown>
          );
        } else {
          result[key] = source[key];
        }
      }
    }

    return result;
  }

  // Performance monitoring
  private recordMetric(
    operationType: string,
    duration: number,
    dataSize: number,
    success: boolean,
    error?: string
  ): void {
    const metric: PerformanceMetrics = {
      operationType,
      duration,
      dataSize,
      timestamp: Date.now(),
      success,
      error,
    };

    this.performanceMetrics.push(metric);

    // Keep only last 1000 metrics
    if (this.performanceMetrics.length > 1000) {
      this.performanceMetrics = this.performanceMetrics.slice(-1000);
    }

    this.emit('metric', metric);
  }

  getPerformanceMetrics(
    operationType?: string,
    timeRange?: { start: number; end: number }
  ): PerformanceMetrics[] {
    let metrics = this.performanceMetrics;

    if (operationType) {
      metrics = metrics.filter(m => m.operationType === operationType);
    }

    if (timeRange) {
      metrics = metrics.filter(
        m => m.timestamp >= timeRange.start && m.timestamp <= timeRange.end
      );
    }

    return metrics;
  }

  getAverageMetrics(operationType?: string): {
    avgDuration: number;
    avgDataSize: number;
    successRate: number;
    totalOperations: number;
  } {
    const metrics = operationType
      ? this.performanceMetrics.filter(m => m.operationType === operationType)
      : this.performanceMetrics;

    if (metrics.length === 0) {
      return {
        avgDuration: 0,
        avgDataSize: 0,
        successRate: 0,
        totalOperations: 0,
      };
    }

    const totalDuration = metrics.reduce((sum, m) => sum + m.duration, 0);
    const totalDataSize = metrics.reduce((sum, m) => sum + m.dataSize, 0);
    const successCount = metrics.filter(m => m.success).length;

    return {
      avgDuration: totalDuration / metrics.length,
      avgDataSize: totalDataSize / metrics.length,
      successRate: (successCount / metrics.length) * 100,
      totalOperations: metrics.length,
    };
  }

  // Event handling
  private emit(event: string, data: unknown): void {
    this.listeners.forEach(listener => listener(event, data));
  }

  addEventListener(listener: (event: string, data: unknown) => void): void {
    this.listeners.add(listener);
  }

  removeEventListener(listener: (event: string, data: unknown) => void): void {
    this.listeners.delete(listener);
  }

  // Cleanup
  async clearStorage(key: string): Promise<void> {
    const storage = this.determineStorage(key);

    if (storage === 'localStorage') {
      localStorage.removeItem(key);
    } else {
      await DatabaseService.delete('persistence', key);
    }
  }

  async clearAllStorage(): Promise<void> {
    // Clear localStorage
    for (const key of this.routing.localStorage) {
      localStorage.removeItem(key);
    }

    // Clear IndexedDB
    await DatabaseService.clear('persistence');
  }

  destroy(): void {
    // Clear all timeouts
    this.syncTimeouts.forEach(timeout => clearTimeout(timeout));
    this.syncTimeouts.clear();

    // Clear queue
    this.operationQueue = [];

    // Clear listeners
    this.listeners.clear();

    // Clear instance
    PersistenceService.instance = null as any;
  }
}

// Export singleton instance getter
export const getPersistenceService = (
  config?: Partial<PersistenceConfig>,
  routing?: Partial<StorageRoutingConfig>
): PersistenceService => {
  return PersistenceService.getInstance(config, routing);
};